import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AppConfigQuery } from '../../../../core/state/app-config/app-config.query';
import { Currency } from '../../currency-selector/currency-country-codes';
import { map } from 'rxjs/operators';
import { Acct } from '../../../state/accts/acct.model';
import { Cacheable } from 'ts-cacheable';
import { Observable } from 'rxjs';
import { Instrument } from 'src/app/shared/state/instruments/instrument.model';
import { ActivatedRoute } from '@angular/router';
import { addHours } from 'date-fns';

type ChartMetric = [Date, number];
type ChartResponse = Array<{
    name: string;
    data: Array<ChartMetric>;
}>;

@Injectable({
    providedIn: 'root'
})
export class ChartDataService {
    private _tempChartVersion = null;
    get tempChartVersion() {
        return this._tempChartVersion ? `/${this._tempChartVersion}` : '';
    }
    constructor(
        private _appConfig: AppConfigQuery,
        private _http: HttpClient,
        private _route: ActivatedRoute
    ) {
        /** Temporary solution to allow setting which version to use for the chart endpoint.
         * If the query parameter chartVersion is set to 'null', the value is removed from the session storage and the default endpoint will be used.
         * If the query parameter chartVersion is set to a value, the value will be stored in session storage and will be appended with the request
         * e.g.`chartVersion=v2` will add v2 to the request until it's reset again by passing another version or setting chartVersion to null.
         */
        const chartVersion =
            this._route.snapshot.queryParamMap.get('chartVersion') ||
            sessionStorage.getItem('sbChartVersion');
        if (chartVersion === 'null') {
            this._tempChartVersion = null;
            sessionStorage.removeItem('sbChartVersion');
        } else if (chartVersion) {
            this._tempChartVersion = chartVersion;
            sessionStorage.setItem('sbChartVersion', chartVersion);
        }
    }
    @Cacheable({
        maxCacheCount: 7,
        maxAge: 1000 * 60 * 15 // 15 minutes TODO: burst cache when transactions or prices are updated or manually refreshed
    })
    getV2(
        accountIds: Array<Acct['accountId']>,
        instrumentIds: Array<Instrument['instrumentId']>,
        fromDate?: Date,
        toDate?: Date,
        baseCurrency?: Currency,
        ignoreAccountOpeningDates = false
    ): Observable<ChartResponse> {
        let params: {
            baseCurrency?: Currency;
            accountIds?: string;
            instrumentIds?: string;
            fromDate?: string;
            toDate?: string;
            ignoreAccountOpeningDates?: boolean;
        } = {
            accountIds: accountIds.join(','),
            instrumentIds: instrumentIds.join(',')
        };
        // if no accounts are pass, return empty array
        if (ignoreAccountOpeningDates) {
            params = {
                ...params,
                ignoreAccountOpeningDates: true
            };
        }
        if (fromDate && toDate) {
            const tzOffset = new Date().getTimezoneOffset() / 60;
            fromDate = addHours(fromDate, -tzOffset);
            toDate = addHours(toDate, -tzOffset);
            params = {
                ...params,
                fromDate: fromDate.toISOString(),
                toDate: toDate.toISOString()
            };
        }
        if (baseCurrency) {
            params = {
                ...params,
                baseCurrency
            };
        }
        return this._http.get<ChartResponse>(
            `${this._appConfig.get().gatewayBaseUrl}/view/chart/v2`,
            {
                params
            }
        );
    }

    /**
     * @deprecated Will be deprecated when we add calculation of key metrics from the new chart V2 endpoint
     * @param accountIds
     * @param instrumentIds
     * @param fromDate
     * @param toDate
     * @param baseCurrency
     * @returns
     */
    @Cacheable({
        maxCacheCount: 7,
        maxAge: 1000 * 60 * 15 // 15 minutes TODO: burst cache when transactions or prices are updated or manually refreshed
    })
    get(
        accountIds: Array<Acct['accountId']>,
        instrumentIds: Array<Instrument['instrumentId']>,
        fromDate?: Date,
        toDate?: Date,
        baseCurrency?: Currency,
        ignoreAccountOpeningDates = false
    ): Observable<ChartResponse> {
        let params: {
            baseCurrency?: Currency;
            accountIds?: string;
            instrumentIds?: string;
            fromDate?: string;
            toDate?: string;
            ignoreAccountOpeningDates?: boolean;
        } = {};
        if (accountIds) {
            params.accountIds = accountIds.join(',');
        }
        if (instrumentIds) {
            params.instrumentIds = instrumentIds.join(',');
        }
        if (fromDate && toDate) {
            const tzOffset = new Date().getTimezoneOffset() / 60;
            fromDate = addHours(fromDate, -tzOffset);
            toDate = addHours(toDate, -tzOffset);
            params = {
                ...params,
                fromDate: fromDate.toISOString(),
                toDate: toDate.toISOString()
            };
        }
        if (baseCurrency) {
            params = {
                ...params,
                baseCurrency
            };
        }
        if (ignoreAccountOpeningDates) {
            params = {
                ...params,
                ignoreAccountOpeningDates: true
            };
        }
        return this._http.get<ChartResponse>(
            `${this._appConfig.get().gatewayBaseUrl}/view/chart`,
            {
                params
            }
        );
    }

    @Cacheable({
        maxCacheCount: 7,
        maxAge: 1000 * 60 * 15 // 15 minutes TODO: burst cache when transactions or prices are updated or manually refreshed
    })
    getSummary(
        accountIds: Array<Acct['accountId']>,
        baseCurrency?: Currency,
        fromDate?: Date,
        toDate?: Date
    ) {
        let params: {
            baseCurrency?: Currency;
            accountIds?: string;
            //fromDate: string;
            //toDate: string;
        } = {
            //fromDate: '2023-12-31T00:00:00.000Z', //fromDate.toISOString(),
            //toDate: '2024-06-06T00:00:00.000Z', //toDate.toISOString(),
            accountIds: accountIds.join(',')
        };
        if (baseCurrency) {
            params = {
                ...params,
                baseCurrency
            };
        }
        return this._http
            .get<Array<any>>(
                `${this._appConfig.get().gatewayBaseUrl}/view/performance/summary`,
                {
                    params
                }
            )
            .pipe(
                map((res) => {
                    const groupedMetrics = res.reduce((acc, val) => {
                        const keyParts = val.key.toLowerCase().split('_');
                        const periodGroup = keyParts[0];
                        const key = keyParts.pop();
                        const value = val.value;
                        if (value !== null) {
                            let name;
                            let hide = false;
                            let order = 0;
                            switch (periodGroup) {
                                case 'daily':
                                    order = 0;
                                    name = '1D';
                                    hide = true;
                                    break;
                                case 'weekly':
                                    order = 1;
                                    name = '1W';
                                    hide = true;
                                    break;
                                case 'month':
                                    name = 'MTD';
                                    order = 2;
                                    break;
                                case 'monthly':
                                    order = 3;
                                    name = '1M';
                                    hide = true;
                                    break;
                                case 'quarterly':
                                    name = '3M';
                                    order = 4;
                                    break;
                                case 'year':
                                    name = 'YTD';
                                    order = 5;
                                    break;
                                case 'yearly':
                                    name = '1Y';
                                    order = 6;
                                    break;
                                case 'three':
                                    name = '3Y';
                                    order = 7;
                                    break;
                                case 'total':
                                    order = 8;
                                    name = 'All';
                                    hide = false;
                                    break;
                                case 'period':
                                    order = 9;
                                    hide = true;
                                    name = 'Custom';
                                    break;
                                case 'market':
                                    order = null;
                                    name = 'marketValue';
                                    hide = true;
                                    break;
                                default:
                                    hide = true;
                                    break;
                            }
                            if (acc[name] === undefined) {
                                acc[name] = {
                                    order,
                                    name,
                                    hide,
                                    [key]: value
                                };
                            } else {
                                acc[name] = {
                                    ...acc[name],
                                    [key]: value
                                };
                            }
                        }
                        return acc;
                    }, {});
                    return groupedMetrics;
                })
            );
    }
}
